/**
 * VFX2: real star catalog (HYG v41, public domain). Loads the build-time
 * `stars.bin` (RA/Dec -> XYZ on a far shell, B-V color, magnitude) and renders
 * recognizable real constellations as a DEPTH-TESTED Points cloud.
 *
 * VFX1 fix: the old procedural field was `transparent + depthTest:false`, so it
 * drew in the transparent pass over opaque planets (see-through bug). This cloud
 * uses depthTest:true and depthWrite:false on a far shell, so opaque bodies
 * correctly occlude stars while stars still composite additively over the sky.
 *
 * Parallax-free in translation (followCamera copies camera world position) but
 * world-fixed in rotation, so turning sweeps the stars.
 */
import * as THREE from 'three';

interface StarsMeta {
  count: number;
  magLimit: number;
  shellR: number;
  stride: number;
}

const STAR_VERT = /* glsl */ `
  attribute float mag;
  varying vec3 vColor;
  void main() {
    vColor = color;
    vec4 mv = modelViewMatrix * vec4(position, 1.0);
    // Brighter (lower mag) -> bigger point. mag ~ -1.5 (Sirius) .. 6.5 (faint).
    float b = clamp((6.5 - mag) / 8.0, 0.05, 1.0);
    gl_PointSize = 1.0 + b * 3.5;
    gl_Position = projectionMatrix * mv;
  }
`;

const STAR_FRAG = /* glsl */ `
  varying vec3 vColor;
  void main() {
    // Soft round point with a bright core.
    vec2 d = gl_PointCoord - vec2(0.5);
    float r = length(d);
    if (r > 0.5) discard;
    float a = smoothstep(0.5, 0.0, r);
    gl_FragColor = vec4(vColor, a);
  }
`;

export const createStarfield = async (
  baseUrl = '/assets/sky/',
): Promise<THREE.Points> => {
  // Missing catalog files (e.g. partial clone) degrade to an empty starfield
  // instead of rejecting and killing the boot sequence.
  let data = new Float32Array(0);
  let n = 0;
  let stride = 7;
  try {
    const metaRes = await fetch(baseUrl + 'stars.meta.json');
    if (!metaRes.ok) throw new Error(`HTTP ${metaRes.status}`);
    const meta = (await metaRes.json()) as StarsMeta;
    const binRes = await fetch(baseUrl + 'stars.bin');
    if (!binRes.ok) throw new Error(`HTTP ${binRes.status}`);
    data = new Float32Array(await binRes.arrayBuffer());
    n = meta.count;
    stride = meta.stride;
  } catch (err) {
    console.warn(`[assets] star catalog missing (${baseUrl}stars.*) — empty starfield`, err);
  }

  const positions = new Float32Array(n * 3);
  const colors = new Float32Array(n * 3);
  const mags = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    const o = i * stride;
    positions[i * 3] = data[o] ?? 0;
    positions[i * 3 + 1] = data[o + 1] ?? 0;
    positions[i * 3 + 2] = data[o + 2] ?? 0;
    colors[i * 3] = data[o + 3] ?? 1;
    colors[i * 3 + 1] = data[o + 4] ?? 1;
    colors[i * 3 + 2] = data[o + 5] ?? 1;
    mags[i] = data[o + 6] ?? 6;
  }

  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
  geo.setAttribute('mag', new THREE.BufferAttribute(mags, 1));

  const mat = new THREE.ShaderMaterial({
    vertexShader: STAR_VERT,
    fragmentShader: STAR_FRAG,
    vertexColors: true,
    transparent: true,
    depthTest: true, // VFX1: opaque bodies occlude stars
    depthWrite: false,
    blending: THREE.AdditiveBlending,
  });

  const points = new THREE.Points(geo, mat);
  points.frustumCulled = false;
  points.renderOrder = 1; // after the background sphere, before HUD
  return points;
};

/**
 * Keep the starfield centred on the camera (parallax-free) WITHOUT inheriting
 * its rotation. Call once per frame after the camera moves.
 */
export const followCamera = (stars: THREE.Object3D, camera: THREE.Camera): void => {
  camera.getWorldPosition(stars.position);
  // rotation left at identity -> world-fixed -> sweeps on turn
};
